/**
 * Agents roster + Trust — read-only view of the configured agent fleet.
 *
 * Reads GET /api/agentic/agents (backend/app/routes/agentic_fleet.py): each
 * configured agent's capability scope, shadowed roles, autonomy settings, and
 * per-decision-type trust metrics (phase, override rate, confidence). On
 * 403/404 from the entitlement gate → NotEnabled.
 */
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Bot } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Heading,
  Icon,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
} from '@smms/design-system';
import { getAgents, type AgentRosterEntry, type AgentTrustMetric } from '../api/agentic';
import { isNotEntitled } from '../api/client';
import NotEnabled from '../components/NotEnabled';

const PHASE_BADGE: Record<string, 'neutral' | 'info' | 'primary' | 'success'> = {
  observer: 'neutral',
  recommendation: 'info',
  supervised: 'primary',
  full_autonomous: 'success',
};

function humanize(s: string): string {
  return s.replaceAll('_', ' ');
}

function pct(fraction: number | null): string {
  if (fraction == null) return '—';
  return `${Math.round(fraction * 100)}%`;
}

function stringList(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  return [];
}

function AgentCard({ agent }: { agent: AgentRosterEntry }) {
  const domain =
    (agent.capability_scope && typeof agent.capability_scope['domain'] === 'string'
      ? (agent.capability_scope['domain'] as string)
      : null) ?? null;
  const roles = agent.role_mapping ? stringList(agent.role_mapping['shadowed_roles']) : [];

  return (
    <Card padding="md">
      <Stack gap="sm">
        <div className="flex items-start gap-3">
          <Icon as={Bot} size="md" className="mt-0.5 text-primary" />
          <div className="min-w-0 flex-1">
            <Heading level={2} className="text-base">
              <Link
                to={`/agents/${agent.agent_id}`}
                className="transition-colors hover:text-primary"
              >
                {humanize(agent.agent_id)}
              </Link>
            </Heading>
            {domain && (
              <Text variant="caption" tone="muted">
                {humanize(domain)}
              </Text>
            )}
          </div>
          <Badge variant="neutral" size="sm">
            {agent.decision_count} decisions
          </Badge>
        </div>

        {roles.length > 0 && (
          <Text variant="caption" tone="muted">
            Shadows: {roles.join(', ')}
          </Text>
        )}

        {agent.trust.length === 0 ? (
          <Text variant="body-sm" tone="muted">
            No trust metrics yet — agent is still observing.
          </Text>
        ) : (
          <div className="mt-1 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-muted">
                  <th className="py-1 pr-3 font-medium">Decision type</th>
                  <th className="py-1 pr-3 font-medium">Phase</th>
                  <th className="py-1 pr-3 font-medium">Decisions</th>
                  <th className="py-1 pr-3 font-medium">Override</th>
                  <th className="py-1 font-medium">Avg conf.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {agent.trust.map((t: AgentTrustMetric) => (
                  <tr key={t.decision_type}>
                    <td className="py-1.5 pr-3">{humanize(t.decision_type)}</td>
                    <td className="py-1.5 pr-3">
                      <Badge variant={PHASE_BADGE[t.current_phase] ?? 'neutral'} size="sm">
                        {humanize(t.current_phase)}
                      </Badge>
                    </td>
                    <td className="py-1.5 pr-3">{t.total_decisions}</td>
                    <td className="py-1.5 pr-3">{pct(t.override_rate)}</td>
                    <td className="py-1.5">
                      {t.avg_confidence != null ? `${Math.round(t.avg_confidence)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Stack>
    </Card>
  );
}

export default function AgentsPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agentic', 'agents'],
    queryFn: getAgents,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="agents-loading">
        <Spinner size="lg" label="Loading agents" />
      </div>
    );
  }

  if (error) {
    if (isNotEntitled(error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load the agent roster"
          description="Something went wrong while loading agents. Try again."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data) return <NotEnabled />;

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <Stack gap="lg">
        <PageHeader
          title="Agents & Trust"
          description="The configured agent fleet for your tenant — capability scope, shadowed roles, autonomy phase, and trust metrics per decision type. Read-only."
        />
        <Section>
          {data.agents.length === 0 ? (
            <Card padding="lg">
              <EmptyState
                compact
                icon={<Icon as={Bot} size="lg" />}
                title="No agents configured"
                description="Once agents are configured for your tenant, they appear here with their trust metrics."
              />
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4" data-testid="agent-roster">
              {data.agents.map(a => (
                <AgentCard key={a.agent_id} agent={a} />
              ))}
            </div>
          )}
        </Section>
      </Stack>
    </div>
  );
}
